/**
 * CwdPickerModal —— 远程浏览桌面端目录树、挑选工作目录的通用弹窗。
 *
 * 走 relay 的 `cwd.suggest` / `cwd.list` 通道，Code 编程页与 Cowork 页共用。
 * 桌面端只允许浏览 `os.homedir()` 子树（见 boclaw-desktop remoteCwdService），
 * 所以起始路径落在 home 之外时会被拒——此时自动回退到 home，避免弹窗直接卡死在报错上。
 */

import React, { useCallback, useEffect, useState } from 'react';
import { i18nService } from '../../services/i18n';
import { relayService } from '../../services/relay';

// 跟随全站 accent 变量（与 CodingView 一致）
const BLUE = 'var(--accent-color, #3B82F6)';
const BLUE_TINT = 'color-mix(in srgb, var(--accent-color, #3B82F6) 12%, transparent)';

interface DirEntry { name: string; path: string }
interface Preset { label: string; path: string }

interface CwdPickerModalProps {
  /** 目标 desktop 客户端 id（relay 路由）。 */
  clientId: string;
  /** 打开时的起始路径（没有则用 desktop 默认 cwd / home）。 */
  initialPath?: string | null;
  /** 选定回调：传具体绝对路径；传 '' 表示「使用默认」（由 desktop 决定）。 */
  onSelect: (path: string) => void;
  onClose: () => void;
}

// 取路径末段作展示（兼容 / 与 \ 分隔）
function baseName(p: string): string {
  if (!p) return '';
  const segs = p.replace(/[\\/]+$/, '').split(/[\\/]/);
  return segs[segs.length - 1] || p;
}

function Spinner({ size = 18 }: { size?: number }) {
  return (
    <span
      className="inline-block animate-spin rounded-full border-2 border-current border-t-transparent align-[-2px]"
      style={{ width: size, height: size, color: BLUE }}
    />
  );
}

const FolderIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);
const UpIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
    <path d="M12 19V6M5 13l7-7 7 7" />
  </svg>
);
const CloseIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const CwdPickerModal: React.FC<CwdPickerModalProps> = ({ clientId, initialPath, onSelect, onClose }) => {
  const [presets, setPresets] = useState<Preset[]>([]);
  const [path, setPath] = useState('');
  const [parent, setParent] = useState<string | null>(null);
  const [entries, setEntries] = useState<DirEntry[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 进入某目录：列子目录 + 记录 parent。返回是否成功，供起始路径逐级回退使用。
  const navigateTo = useCallback(async (target: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const res = await relayService.listCwd(clientId, target);
      setPath(res.path || target);
      setParent(res.parent ?? null);
      setEntries(res.entries ?? []);
      setTruncated(!!res.truncated);
      return true;
    } catch (e) {
      setError(`${i18nService.t('codeCwdLoadFailed')}: ${(e as Error).message}`);
      return false;
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  // 首次：拉 presets + 默认 cwd，再定位到起始路径
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const sg = await relayService.suggestCwd(clientId);
        if (!alive) return;
        setPresets(sg.presets ?? []);
        // 起始路径逐级回退：调用方指定 → 桌面默认 cwd → home。
        // 桌面端把浏览限制在 home 子树内，默认 cwd 若落在 home 之外会被直接拒掉；
        // 不回退的话用户看到的就是一个只有报错、无法导航的空弹窗。
        const candidates = [...new Set(
          [initialPath, sg.defaultCwd, sg.home]
            .map((p) => p?.trim())
            .filter((p): p is string => !!p),
        )];
        for (const candidate of candidates) {
          if (!alive) return;
          if (await navigateTo(candidate)) return;
        }
      } catch (e) {
        if (alive) {
          setError(`${i18nService.t('codeCwdLoadFailed')}: ${(e as Error).message}`);
          setLoading(false);
        }
      }
    })();
    return () => { alive = false; };
  }, [clientId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="flex max-h-[78vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white dark:bg-claude-darkSurface"
        style={{ boxShadow: '0 24px 60px rgba(28,48,98,.28)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部 */}
        <div className="flex items-center gap-2 border-b border-black/[0.06] dark:border-white/10 px-4 py-3">
          <span style={{ color: BLUE }}>{FolderIcon}</span>
          <span className="text-sm font-semibold text-claude-text dark:text-claude-darkText">{i18nService.t('codeCwdTitle')}</span>
          <button type="button" onClick={onClose}
            className="ml-auto flex h-7 w-7 items-center justify-center rounded-lg text-claude-textSecondary hover:bg-claude-surfaceHover dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover">
            {CloseIcon}
          </button>
        </div>

        {/* 预设目录 chips */}
        {presets.length > 0 && (
          <div className="flex flex-wrap gap-1.5 border-b border-black/[0.06] dark:border-white/10 px-4 py-2.5">
            {presets.map((p) => (
              <button key={p.path} type="button" onClick={() => navigateTo(p.path)}
                className={`rounded-full px-2.5 py-1 text-xs ${p.path === path ? 'font-medium' : 'text-claude-textSecondary dark:text-claude-darkTextSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover'}`}
                style={p.path === path ? { color: BLUE, background: BLUE_TINT } : { border: '1px solid rgba(0,0,0,0.06)' }}>
                {p.label}
              </button>
            ))}
          </div>
        )}

        {/* 当前路径 */}
        <div className="border-b border-black/[0.06] dark:border-white/10 px-4 py-2">
          <div className="truncate rounded-lg bg-black/[0.04] dark:bg-white/5 px-2.5 py-1.5 font-mono text-[12px] text-claude-textSecondary dark:text-claude-darkTextSecondary" title={path}>
            {path || '—'}
          </div>
        </div>

        {/* 子目录列表 */}
        <div className="min-h-[160px] flex-1 overflow-y-auto px-2 py-1.5">
          {loading ? (
            <div className="flex h-40 items-center justify-center"><Spinner /></div>
          ) : error ? (
            <div className="px-3 py-6 text-center text-xs text-red-500">{error}</div>
          ) : (
            <>
              {parent && (
                <button type="button" onClick={() => navigateTo(parent)}
                  className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-claude-textSecondary dark:text-claude-darkTextSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover">
                  <span style={{ color: BLUE }}>{UpIcon}</span>
                  {i18nService.t('codeCwdParent')}
                </button>
              )}
              {entries.map((d) => (
                <button key={d.path} type="button" onClick={() => navigateTo(d.path)}
                  className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-claude-text dark:text-claude-darkText hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover">
                  <span className="text-claude-textSecondary dark:text-claude-darkTextSecondary">{FolderIcon}</span>
                  <span className="flex-1 truncate text-left">{d.name}</span>
                </button>
              ))}
              {entries.length === 0 && (
                <div className="px-3 py-6 text-center text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">{i18nService.t('codeCwdNoSub')}</div>
              )}
              {truncated && (
                <div className="px-3 py-2 text-center text-[11px] text-claude-textSecondary dark:text-claude-darkTextSecondary">{i18nService.t('codeCwdTruncated')}</div>
              )}
            </>
          )}
        </div>

        {/* 底部操作 */}
        <div className="flex items-center gap-2 border-t border-black/[0.06] dark:border-white/10 px-4 py-3">
          <button type="button" onClick={() => onSelect('')}
            className="rounded-lg border border-black/[0.1] dark:border-white/15 px-3 py-1.5 text-sm text-claude-text dark:text-claude-darkText hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover">
            {i18nService.t('codeCwdUseDefault')}
          </button>
          <button type="button" disabled={!path || loading} onClick={() => onSelect(path)}
            className="ml-auto rounded-lg px-3.5 py-1.5 text-sm font-medium text-white disabled:opacity-40"
            style={{ background: BLUE }}>
            {i18nService.t('codeCwdSelectThis')}
            {path && <span className="ml-1 font-normal opacity-80">· {baseName(path)}</span>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CwdPickerModal;
